<template>
  <span
    ref="icone"
    :id="id"
    class="hint-info"
    role="button"
    tabindex="0"
    :aria-label="`Ajuda: ${texto}`"
    :aria-expanded="visivel ? 'true' : 'false'"
    @mouseenter="mostra"
    @mouseleave="esconde"
    @focus="mostra"
    @blur="esconde"
    @keydown.esc="esconde"
    @click.prevent="alterna"
  >
    <CIcon name="cil-info" size="sm" aria-hidden="true" />
    <span
      v-if="visivel"
      ref="balao"
      class="hint-info__balao"
      role="tooltip"
      :style="balaoStyle"
    >{{ texto }}</span>
  </span>
</template>

<script>
const LARGURA_BALAO = 260;

/** Respiro entre o balao e a borda da janela. */
const MARGEM_BALAO = 8;

/** Usada só na primeira medida, antes de o balao existir no DOM. */
const ALTURA_ESTIMADA_BALAO = 120;

let sequencia = 0;

/**
 * Ícone "i" com balão explicativo em position: fixed — escapa do recorte das
 * modais (tab-content com overflow) e nunca estoura a tela: abre para cima ou
 * para baixo conforme o espaço e é limitado às bordas laterais do viewport.
 *
 * Alcançável por teclado (Tab abre, Esc fecha) e anunciado por leitor de tela
 * via aria-label.
 */
export default {
  name: "HintInfo",
  props: {
    texto: { type: String, required: true },
  },
  data() {
    sequencia += 1;

    return {
      id: `hint-info-${sequencia}`,
      visivel: false,
      fixado: false,
      balaoStyle: {},
    };
  },
  beforeDestroy() {
    this.fecha();
  },
  methods: {
    /**
     * Recalcula a posição do balão a partir do ícone. Chamado também no scroll
     * e no resize: o balão é fixed, então sem isso ele descola do "i" quando o
     * conteúdo da modal rola com o ponteiro parado sobre o ícone.
     */
    posiciona() {
      const icone = this.$refs.icone;
      if (!icone) return;

      const rect = icone.getBoundingClientRect();

      const left = Math.max(
        8,
        Math.min(
          rect.left + rect.width / 2 - LARGURA_BALAO / 2,
          window.innerWidth - LARGURA_BALAO - 8
        )
      );

      // Decide pelo espaco que sobra dos dois lados, medindo o balao ja
      // renderizado. Antes a conta olhava so o espaco acima (`rect.top > 110`),
      // entao um campo no rodape abria o balao para baixo mesmo sem caber e
      // empurrava a pagina, criando barra de rolagem.
      const balao = this.$refs.balao;
      const altura = balao ? balao.offsetHeight : ALTURA_ESTIMADA_BALAO;
      const espacoAbaixo = window.innerHeight - rect.bottom - MARGEM_BALAO;
      const espacoAcima = rect.top - MARGEM_BALAO;
      const abreParaCima = espacoAbaixo < altura && espacoAcima > espacoAbaixo;

      this.balaoStyle = abreParaCima
        ? {
            position: "fixed",
            left: `${left}px`,
            bottom: `${window.innerHeight - rect.top + 6}px`,
            width: `${LARGURA_BALAO}px`,
          }
        : {
            position: "fixed",
            left: `${left}px`,
            top: `${rect.bottom + 6}px`,
            width: `${LARGURA_BALAO}px`,
          };
    },
    mostra() {
      if (this.visivel) return;

      this.posiciona();
      window.addEventListener("scroll", this.posiciona, true);
      window.addEventListener("resize", this.posiciona);
      this.visivel = true;
      // a primeira conta usa a altura estimada; com o balao renderizado da para
      // medir a real e corrigir o lado, se necessario
      this.$nextTick(this.posiciona);
    },
    /**
     * Sair com o ponteiro nao fecha um balao aberto no clique: so o hover e
     * que e passageiro. Sem isso o clique nao servia para nada — bastava mover
     * o mouse para ler ate o fim e o texto sumia.
     */
    esconde() {
      if (this.fixado) return;

      this.fecha();
    },
    fecha() {
      window.removeEventListener("scroll", this.posiciona, true);
      window.removeEventListener("resize", this.posiciona);
      document.removeEventListener("click", this.cliqueFora, true);
      this.fixado = false;
      this.visivel = false;
    },
    alterna() {
      if (this.fixado) {
        this.fecha();
        return;
      }

      this.mostra();
      this.fixado = true;
      // fecha ao clicar em qualquer outro lugar da tela
      document.addEventListener("click", this.cliqueFora, true);
    },
    cliqueFora(evento) {
      const icone = this.$refs.icone;

      if (icone && !icone.contains(evento.target)) {
        this.fecha();
      }
    },
  },
};
</script>

<style scoped>
/* Cores vindas das CSS vars do tema (assets/scss/style.scss) */
.hint-info {
  color: var(--text-subtle);
  cursor: pointer;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  vertical-align: middle;
}

.hint-info:hover {
  color: var(--primary);
}

.hint-info:focus-visible {
  color: var(--primary);
  outline: 2px solid var(--primary);
  outline-offset: 2px;
  border-radius: 2px;
}

/* Balao claro, com os mesmos valores do popover que os toggles e os cards
   usavam antes de passarem a reaproveitar este componente. */
.hint-info__balao {
  /* acima do menu do ComboCodigo (10000) */
  z-index: 10001;
  background: #fff;
  color: #344054;
  border: 1px solid #dddce9;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  font-size: 12px;
  font-weight: normal;
  line-height: 1.6;
  padding: 12px 14px;
  border-radius: 8px;
  white-space: normal;
}
</style>
